import React from 'react';
import { AlertCircle, X } from 'lucide-react';
import useEscapeKey from '../hooks/useEscapeKey';

/**
 * ValidationModal — in-app modal for save validation errors.
 * Props:
 *   isOpen: bool
 *   title: string
 *   messages: string[]
 *   onClose: () => void
 *   onGoToTask: () => void
 *   closeLabel: string
 *   goToTaskLabel: string
 */
const ValidationModal = ({ isOpen, title, messages, onClose, onGoToTask, closeLabel = 'Fermer', goToTaskLabel = 'Aller à la tâche' }) => {
  useEscapeKey(() => { onClose?.(); }, !!isOpen);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      {/* Modal panel */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <AlertCircle size={18} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 leading-snug truncate">{title}</h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body — error list */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <ul className="space-y-2">
            {messages?.map((msg, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-orange-400 mt-1.5" />
                <span>{msg}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer — action buttons */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {closeLabel}
          </button>
          <button
            type="button"
            onClick={onGoToTask}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60"
          >
            {goToTaskLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ValidationModal;
